import React from 'react';
import { useDraggable } from '@dnd-kit/core';

const DraggableHandCard = ({ 
  card, 
  index, 
  setSelectedCard, 
  setShowCardInfoModal, 
  globalIsDragging, // Renamed to avoid conflict with useDraggable hook
  totalCards, // Add this prop to calculate dynamic spacing
  isHighlighted // New prop for highlighting newly added cards
}) => {
  // ============================================================================
  // HAND CARD DRAG IMPLEMENTATION
  // ============================================================================
  // 
  // This component makes individual hand cards draggable with:
  // 1. Unique IDs that don't conflict with zone IDs
  // 2. Proper data structure for the centralized drag handler
  // 3. Visual feedback during drag operations
  // 4. Integration with the centralized drag and drop logic
  // 5. Highlighting for newly added cards
  //
  // Key features:
  // - useDraggable: Creates draggable element with unique ID
  // - Unique ID format: "drag-hand-{index}" to avoid zone conflicts
  // - Data structure: Provides source information for drop handling
  // - Visual feedback: Shows drag state and transform effects
  // - Highlighting: Special visual effect for newly added cards
  // ============================================================================

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `drag-hand-${card.id}-${index}`, // Use both card ID and index for unique identification
    data: {
      card,
      from: 'hand', // Source location
      index, // Position in hand array
      cardId: card.id, // Unique card identifier for stable tracking
    },
  });

  return (
    <img
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      src={card.card_images?.[0]?.image_url}
      alt={card.name}
      className={`hand-card ${isHighlighted ? 'highlighted' : ''}`}
      data-dragging={isDragging}
      style={{
        width: '80px',
        height: '115px',
        borderRadius: '8px',
        border: isDragging 
          ? '3px solid #ff6b35' 
          : isHighlighted
            ? '3px solid #ff0000'
            : '2px solid rgba(255, 255, 255, 0.3)',
        boxShadow: isDragging 
          ? '0 8px 25px rgba(255, 107, 53, 0.6), 0 4px 15px rgba(0, 0, 0, 0.3)' 
          : isHighlighted
            ? '0 0 20px rgba(255, 0, 0, 0.6), 0 4px 15px rgba(0, 0, 0, 0.3)'
            : '0 4px 15px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
        opacity: isDragging ? 0.9 : 1,
        transition: 'all 0.3s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        transform: transform ? `translate3d(${transform.x || 0}px, ${transform.y || 0}px, 0) scale(1.1) rotate(8deg)` : 'scale(1)',
        zIndex: isDragging ? 9999 : isHighlighted ? 10000 : index, // Stack cards by index when not dragging
        filter: isDragging 
          ? 'brightness(1.2) saturate(1.3)' 
          : isHighlighted
            ? 'brightness(1.3) saturate(1.5) hue-rotate(0deg)'
            : 'brightness(1) saturate(1)',
        // Add horizontal spacing between cards - proper spacing for horizontal scrolling
        marginLeft: index === 0 ? '0' : '8px', // Consistent spacing between cards
        // AGGRESSIVE OVERRIDES to prevent vertical stacking
        display: 'inline-block !important',
        float: 'none !important',
        clear: 'none !important',
        verticalAlign: 'top !important',
        // CRITICAL: Prevent wrapping and ensure single row
        whiteSpace: 'nowrap !important',
        wordWrap: 'normal !important',
        wordBreak: 'keep-all !important',
        // Force horizontal flow
        flexDirection: 'row !important',
        flexWrap: 'nowrap !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        // Prevent any CSS transforms
        writingMode: 'horizontal-tb !important',
        direction: 'ltr !important',
        textOrientation: 'mixed !important',
        // Ensure proper positioning
        top: 'auto !important',
        bottom: 'auto !important',
        left: 'auto !important',
        right: 'auto !important',
      }}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.target.style.transform = 'scale(1.15) translateY(-8px)';
          e.target.style.zIndex = 1000 + index; // Higher z-index on hover
          e.target.style.filter = 'brightness(1.1) saturate(1.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.target.style.transform = 'scale(1)';
          e.target.style.zIndex = index; // Return to normal stacking
          e.target.style.filter = 'brightness(1) saturate(1)';
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        // Only show card info modal if not dragging
        if (!isDragging && setSelectedCard && setShowCardInfoModal) {
          setSelectedCard(card);
          setShowCardInfoModal(true);
        }
      }}
      onContextMenu={(e) => e.preventDefault()}
      draggable={false}
    />
  );
};

export default DraggableHandCard; 